import { useEffect, useState } from 'react'

import { getOperationName } from '@apollo/client/utilities'
import { ActionIcon, Box, Modal } from '@mantine/core'
import { Step } from 'types/graphql'

import { navigate, routes } from '@redwoodjs/router'

import DocumentPreview from 'src/components/Overlays/DocumentPreview/DocumentPreview'
import StepStatusMenu from 'src/components/Overlays/StepStatusMenu/StepStatusMenu'
import { GET_APPLICANT_BY_ID } from 'src/graphql/applicants.graphql'
import {
  GET_SIDEBAR_CHECKLIST_STEPS,
  GET_STEPS_GROUP_BY_TYPE,
} from 'src/graphql/steps.graphql'
import { DocumentStatus, DocumentUserStatus } from 'src/graphql/types/documents'
import { getStepLabel } from 'src/lib/formatters'
import { cn } from 'src/utils'

import StepFindingsBadge from './StepFindingsBadge'

interface StepItemProps {
  step: Step
  // ENG-4328: applicantId is what StepFindingsBadge uses to fetch the
  // application license and MVR data — everything the rule engine needs.
  // Optional so the item can still render its label + status even without
  // AI context (e.g. list previews); the badge just won't appear.
  applicantId?: string
}
const StepItem = ({ step, applicantId }: StepItemProps) => {
  const activeDocuments = (step.documents ?? []).filter(
    (doc) => doc.status !== DocumentStatus.ARCHIVED
  )
  const hasPendingReview = activeDocuments.some(
    (doc) => doc.userStatus === DocumentUserStatus.PENDING_REVIEW
  )
  const noDocuments = activeDocuments.length === 0

  // Default open when something needs review (preserves the old always-visible
  // pending-review hint); otherwise collapsed to keep the checklist scannable.
  const [expanded, setExpanded] = useState(hasPendingReview)
  const [previewDoc, setPreviewDoc] = useState<
    (typeof activeDocuments)[number] | null
  >(null)

  // Re-expand when a document later becomes pending-review while this stays
  // mounted (e.g. an upload/refetch), so the review hint isn't hidden behind a
  // collapsed caret. Only fires on the false→true transition, so a manual
  // collapse of an already-pending step is respected.
  useEffect(() => {
    if (hasPendingReview) setExpanded(true)
  }, [hasPendingReview])

  const refetchQueries = applicantId
    ? [
        // The sidebar checklist itself (steps + documents + AI findings)
        {
          query: GET_SIDEBAR_CHECKLIST_STEPS,
          variables: { applicantId },
        },
        // Step status changes can move the applicant's hiring stage, which
        // the sidebar header reads from the (lean) applicant query.
        { query: GET_APPLICANT_BY_ID, variables: { id: applicantId } },
        // Keep the DQF checklist (Documents tab) in sync when it's mounted
        // alongside the sidebar — Step isn't normalized by stepId, so the
        // mutation result won't auto-update that list. Refetch by operation
        // name so it's a no-op when the tab isn't open.
        getOperationName(GET_STEPS_GROUP_BY_TYPE),
      ]
    : undefined

  const handlePreview = (doc: (typeof activeDocuments)[number]) => {
    if (!applicantId) return
    setPreviewDoc(doc)
  }

  return (
    <>
      <Box className="flex w-full flex-row items-center gap-2 py-0.5 text-xs">
        <div className="flex w-full flex-col gap-2 ">
          <div className="flex min-h-[20px] flex-row items-center justify-between text-ellipsis text-nowrap font-medium text-doubleNickel-gray-500">
            <Box
              className="flex flex-row items-center hover:cursor-pointer hover:underline"
              onClick={() =>
                navigate(
                  routes.applicantDetails({
                    id: applicantId,
                    tab: 'documents',
                    dqf: step.customStepId ?? step.type,
                  })
                )
              }
            >
              {getStepLabel(step)}
              {applicantId && (
                <StepFindingsBadge step={step} applicantId={applicantId} />
              )}
            </Box>
            <div className="flex flex-row items-center gap-1">
              <StepStatusMenu
                stepId={step.stepId}
                status={step.status}
                size="xs"
                refetchQueries={refetchQueries}
              />
              <ActionIcon
                variant="subtle"
                color="gray"
                className="!h-4 !min-h-0 !w-4 !min-w-0 disabled:!bg-transparent"
                disabled={noDocuments}
                aria-label={
                  expanded ? 'Collapse documents' : 'Expand documents'
                }
                onClick={(e) => {
                  e.stopPropagation()
                  setExpanded((prev) => !prev)
                }}
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                  className={cn(
                    'h-3 w-3 transition-transform',
                    noDocuments
                      ? 'text-doubleNickel-gray-300'
                      : 'text-doubleNickel-brand-500',
                    expanded && 'rotate-90'
                  )}
                >
                  <path d="M6 4l4 4-4 4z" />
                </svg>
              </ActionIcon>
            </div>
          </div>
          {expanded && (
            <div className="flex flex-col text-doubleNickel-gray-600">
              {activeDocuments.map((doc) => (
                <Box
                  key={doc.documentId}
                  onClick={(e) => {
                    e.stopPropagation()
                    handlePreview(doc)
                  }}
                  className="flex flex-row items-center justify-between gap-1 text-nowrap rounded-lg py-1.5 pl-6 text-doubleNickel-gray-900 hover:bg-doubleNickel-gray-50"
                >
                  <div className="flex flex-row items-center gap-2 text-ellipsis text-doubleNickel-gray-500 hover:cursor-pointer hover:underline">
                    {doc.fileName}
                  </div>
                </Box>
              ))}
            </div>
          )}
        </div>
      </Box>

      {previewDoc && applicantId && (
        <Modal
          size="90rem"
          lockScroll={false}
          // Conditionally mounted (can't use stackId), so an explicit zIndex is
          // needed to render above stack-managed overlays — e.g. the applicant
          // drawer this sidebar can live inside. Mirrors AttachmentsMenu.
          zIndex={210}
          classNames={{
            content: 'min-h-96 h-[100%]',
            body: 'flex h-[95%] flex-col overflow-auto',
          }}
          transitionProps={{ transition: 'fade', duration: 200 }}
          opened
          onClose={() => setPreviewDoc(null)}
          withCloseButton={false}
        >
          <DocumentPreview
            documentId={previewDoc.documentId}
            applicantId={applicantId}
            showClosebutton={true}
            showBackButton={false}
            onBack={() => setPreviewDoc(null)}
            showActions={false}
            refetchQueries={refetchQueries ?? []}
          />
        </Modal>
      )}
    </>
  )
}

export default StepItem
